import type {
    CurrencyCode} from "@@/shared/currencies/currencies";
import {
    currencyCodeZodSchema,
} from "@@/shared/currencies/currencies";
import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    OrderGroupId} from "@@/shared/order-groups/order-group-id";
import {
    orderGroupIdZodSchema,
} from "@@/shared/order-groups/order-group-id";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { PaymentId} from "../payment-id";
import { paymentIdFactory, paymentIdZodSchema } from "../payment-id";
import type {
    PaymentProvider} from "../payment-providers";
import {
    paymentProviderZodSchema,
} from "../payment-providers";

type StripePaymentPending = {
    _id: PaymentId;
    _type: "STRIPE_PAYMENT";
    _private: {
        externalCustomerId: string;
        externalPaymentId: string;
        delayed: {
            value: boolean;
            secret?: string;
        };
    };
    amount: number;
    _createdAt: IsoAndUnixTimestamp;
    currencyCode: CurrencyCode;
    orderGroupId: OrderGroupId;
    paymentProvider: PaymentProvider;
    status: "PENDING";
    userId: UserId;
};

const stripePaymentPendingFactory = (
    params: Except<
        StripePaymentPending,
        "_id" | "_type" | "status" | "_createdAt"
    >,
) => {
    const payment: StripePaymentPending = {
        ...params,
        _id: paymentIdFactory(),
        _type: "STRIPE_PAYMENT",
        status: "PENDING",
        _createdAt: nowIsoAndUnix(),
    };

    return payment;
};

const stripePaymentPendingZodSchema: z.ZodSchema<StripePaymentPending> = z
    .object({
        _id: paymentIdZodSchema,
        _type: z.literal("STRIPE_PAYMENT"),
        _private: z.object({
            externalCustomerId: z.string(),
            externalPaymentId: z.string(),
            delayed: z.object({
                value: z.boolean(),
                secret: z.string().optional(),
            }),
        }),
        amount: z.number().min(0),
        _createdAt: isoAndUnixZodSchema,
        currencyCode: currencyCodeZodSchema,
        orderGroupId: orderGroupIdZodSchema,
        paymentProvider: paymentProviderZodSchema,
        status: z.literal("PENDING"),
        userId: userIdZodSchema,
    })
    .refine(value => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        value._private.delayed.value === true &&
            !value._private.delayed.secret?.length,
            {
                message:
                    "delayed.secret is required when delayed.value is true",
            };
    })
    .refine(value => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        value._private.delayed.value === false &&
            !!value._private.delayed.secret,
            {
                message:
                    "delayed.secret cannot be set when delayed.value is false",
            };
    });

export { stripePaymentPendingFactory, stripePaymentPendingZodSchema };
export type { StripePaymentPending };
